import React, { useState, useEffect, useCallback } from 'react';
import { Search, Package, X, Loader2, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Select } from './ui/select';
import { Product } from '../types';
import { searchProducts } from '../services/productService';
import { useProductCategories } from '../hooks/useProductCategories';

interface ProductPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (product: Product) => void;
  excludeProductIds?: string[];
  currentProductId?: string;
  context?: 'variant-linking' | 'order-editing';
}

export function ProductPicker({ 
  isOpen, 
  onClose, 
  onSelect, 
  excludeProductIds = [],
  currentProductId,
  context = 'variant-linking' 
}: ProductPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Get categories and brands for filters
  const { categories } = useProductCategories();
  const [brands, setBrands] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedCategory('All');
      setSelectedBrand('All');
      setProducts([]);
      setFilteredProducts([]);
      setHasSearched(false);
      setCurrentPage(1);
      setShowFilters(false);
    }
  }, [isOpen]);

  // Extract unique brands from products
  useEffect(() => {
    const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
    setBrands(uniqueBrands);
  }, [products]);

  // Comprehensive search function
  const performSearch = useCallback(async () => {
    if (!searchQuery.trim() && selectedCategory === 'All' && selectedBrand === 'All') {
      setProducts([]);
      setFilteredProducts([]);
      setHasSearched(false);
      return;
    }
    
    setLoading(true);
    setHasSearched(true);
    
    try {
      // Use the same comprehensive search logic as the Products page
      const searchResults = await searchProducts(
        searchQuery,
        selectedCategory !== 'All' ? selectedCategory : undefined,
        selectedBrand !== 'All' ? selectedBrand : undefined
      );
      
      // Filter out excluded products and disabled products
      const filteredResults = searchResults.products.filter(product => 
        !excludeProductIds.includes(product.id) && 
        !product.disabled
      );
      
      setProducts(filteredResults);
      
      // Apply pagination
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedResults = filteredResults.slice(startIndex, endIndex);
      setFilteredProducts(paginatedResults);
      
      // Calculate total pages
      setTotalPages(Math.ceil(filteredResults.length / pageSize));
      
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedBrand, excludeProductIds, currentPage, pageSize]);

  // Update pagination when products change
  useEffect(() => {
    if (products.length > 0) {
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedResults = products.slice(startIndex, endIndex);
      setFilteredProducts(paginatedResults);
      setTotalPages(Math.ceil(products.length / pageSize));
    }
  }, [products, currentPage, pageSize]);

  // Trigger search when search query or filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [performSearch]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedBrand]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  const handleProductSelect = (product: Product) => {
    onSelect(product);
    onClose();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedBrand('All');
    setCurrentPage(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10004] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Select Product</h2>
                <p className="text-white/80 text-sm">
                  {context === 'order-editing' 
                    ? 'Choose a product to add to the order' 
                    : 'Choose a product to link as a variation'
                  }
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-white hover:bg-white/20 h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0 space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Search products by name, description, category, or brand..."
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20"
                autoFocus
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="h-12 px-4 rounded-xl border-gray-200 hover:bg-gray-50"
            >
              <Filter className="h-4 w-4" />
            </Button>
            <Button
              onClick={performSearch}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 h-12 rounded-xl"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="flex gap-3 pt-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:border-purple-600 focus:ring-purple-600/20"
                >
                  <option value="All">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Brand</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:border-purple-600 focus:ring-purple-600/20"
                >
                  <option value="All">All Brands</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  size="sm"
                  className="h-10 px-3 text-xs"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Active filters display */}
          {(searchQuery || selectedCategory !== 'All' || selectedBrand !== 'All') && (
            <div className="flex gap-2 flex-wrap">
              {searchQuery && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  Search: "{searchQuery}"
                </Badge>
              )}
              {selectedCategory !== 'All' && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Category: {selectedCategory}
                </Badge>
              )}
              {selectedBrand !== 'All' && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Brand: {selectedBrand}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {!hasSearched ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Enter search terms or use filters to find products</p>
              <p className="text-gray-400 text-sm mt-1">Search by name, description, category, or brand</p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Searching products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No products found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search terms or filters</p>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {/* Results header */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, products.length)} of {products.length} products
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      {currentPage} of {totalPages}
                    </span>
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Product list */}
              <div className="space-y-3">
                {filteredProducts.map((product) => (
                  <Card 
                    key={product.id}
                    className="cursor-pointer hover:shadow-md transition-all duration-200 border-gray-200 hover:border-purple-300"
                    onClick={() => handleProductSelect(product)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-product.png';
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">{product.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">
                              ${product.price.toFixed(2)}
                            </span>
                            {product.category && (
                              <Badge variant="outline" className="text-xs">
                                {product.category}
                              </Badge>
                            )}
                            {product.brand && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                {product.brand}
                              </Badge>
                            )}
                            {product.stockQuantity !== undefined && (
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  product.stockQuantity === 0 
                                    ? 'bg-red-50 text-red-700 border-red-200'
                                    : product.stockQuantity <= 10
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                    : 'bg-green-50 text-green-700 border-green-200'
                                }`}
                              >
                                {product.stockQuantity === 0 ? 'Out of Stock' : `${product.stockQuantity} in stock`}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Bottom pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs"
                    >
                      First
                    </Button>
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const startPage = Math.max(1, currentPage - 2);
                      const pageNum = startPage + i;
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-xs"
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 